import { useCitySeoInfo } from '@/entities/city/api/useCitySeoInfo.ts';
import { useTourCitySeoInfo } from '@/entities/city/api/useTourCitySeoInfo.ts';
import { useTourCityTagSeoInfo } from '@/entities/city/api/useTourCityTagSeoInfo.ts';
import { useCountryTagSeoInfo } from '@/entities/region/api/useCountryTagSeoInfo.ts';
import { useRegionTagSeoInfo } from '@/entities/region/api/useRegionTagSeoInfo.ts';
import { useTourInCountryFromCitySeoInfo } from '@/entities/region/api/useTourInCountryFromCitySeoInfo.ts';
import { useTourInRegionFromCitySeoInfo } from '@/entities/region/api/useTourInRegionFromCitySeoInfo.ts';
import { useTourSeoInfo } from '@/entities/region/api/useTourSeoInfo.ts';
import { extractTagSlugFromRouteParam } from '@/entities/rubrics/utils/tags.ts';
import { useListingRouteParams } from '@/pages/listing/composables/useListingRouteParams.ts';
import { VerticalsValues } from '@/shared/types/verticals.ts';
import { useHead } from '@unhead/vue';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

interface Params {
  is404: MaybeRefOrGetter<boolean>;
  route: RouteLocationNormalizedLoaded;
  vertical: MaybeRefOrGetter<string>;
}

export function useListingNotFoundSeo({ is404, route, vertical }: Params) {
  const { isFromCityPage } = useListingRouteParams();

  const getRouteParam = (name: string) => {
    const param = route.params[name];

    if (Array.isArray(param)) {
      return param[0] || '';
    }

    return param || '';
  };

  const citySlug = computed(() => getRouteParam('citySlug'));
  const regionSlug = computed(() => getRouteParam('regionSlug'));
  const countrySlug = computed(() => getRouteParam('countrySlug') || getRouteParam('country'));
  const currentTag = computed(() => extractTagSlugFromRouteParam(getRouteParam('tag') || 'all') || 'all');
  const isAllTag = computed(() => currentTag.value === 'all');
  const page = computed(() => Number(route.query?.page ?? 1));

  const getIs404 = computed(() => toValue(is404));
  const getVertical = computed(() => toValue(vertical));

  const isExcursionCitySeoEnabled = computed(
    () =>
      getIs404.value &&
      getVertical.value === VerticalsValues.EXCURSION &&
      route.meta?.destinationType === 'city' &&
      Boolean(citySlug.value),
  );

  const isTourCitySeoEnabled = computed(
    () =>
      getIs404.value &&
      getVertical.value === VerticalsValues.TOURS &&
      route.meta?.destinationType === 'city' &&
      Boolean(citySlug.value),
  );

  const isTourRegionSeoEnabled = computed(
    () =>
      getIs404.value &&
      getVertical.value === VerticalsValues.TOURS &&
      route.meta?.destinationType === 'region' &&
      Boolean(regionSlug.value),
  );

  const isTourCountrySeoEnabled = computed(
    () =>
      getIs404.value &&
      getVertical.value === VerticalsValues.TOURS &&
      route.meta?.destinationType === 'country' &&
      Boolean(countrySlug.value),
  );

  const { citySeoData } = useCitySeoInfo(citySlug, currentTag, page, isExcursionCitySeoEnabled);
  const { tourCitySeoData } = useTourCitySeoInfo(
    citySlug,
    computed(() => isTourCitySeoEnabled.value && isAllTag.value),
  );
  const { cityTagSeoData } = useTourCityTagSeoInfo(
    citySlug,
    currentTag,
    computed(() => isTourCitySeoEnabled.value && !isAllTag.value),
  );
  const { tourSeoData: regionSeoData } = useTourSeoInfo(
    'region_tours',
    regionSlug,
    computed(() => isTourRegionSeoEnabled.value && !isFromCityPage.value && isAllTag.value),
  );
  const { regionTagSeoData } = useRegionTagSeoInfo(
    computed(() => `${regionSlug.value}__${currentTag.value}`),
    computed(() => isTourRegionSeoEnabled.value && !isFromCityPage.value && !isAllTag.value),
  );
  const { tourInRegionFromCitySeoData } = useTourInRegionFromCitySeoInfo(
    regionSlug,
    citySlug,
    computed(() => isTourRegionSeoEnabled.value && isFromCityPage.value && Boolean(citySlug.value)),
  );
  const { tourSeoData: countrySeoData } = useTourSeoInfo(
    'country_tours',
    countrySlug,
    computed(() => isTourCountrySeoEnabled.value && !isFromCityPage.value && isAllTag.value),
  );
  const { countryTagSeoData } = useCountryTagSeoInfo(
    computed(() => `${countrySlug.value}__${currentTag.value}`),
    computed(() => isTourCountrySeoEnabled.value && !isFromCityPage.value && !isAllTag.value),
  );
  const { tourInCountryFromCitySeoData } = useTourInCountryFromCitySeoInfo(
    countrySlug,
    citySlug,
    computed(() => isTourCountrySeoEnabled.value && isFromCityPage.value && Boolean(citySlug.value)),
  );

  const tourCitySeo = computed(() => (isAllTag.value ? tourCitySeoData.value?.seo : cityTagSeoData.value?.seo));
  const tourRegionSeo = computed(() => {
    if (isFromCityPage.value) {
      return tourInRegionFromCitySeoData.value?.seo;
    }

    return isAllTag.value ? regionSeoData.value?.seo : regionTagSeoData.value?.seo;
  });
  const tourCountrySeo = computed(() => {
    if (isFromCityPage.value) {
      return tourInCountryFromCitySeoData.value?.seo;
    }

    return isAllTag.value ? countrySeoData.value?.seo : countryTagSeoData.value?.seo;
  });
  const seoData = computed(() => {
    return [
      { enabled: isExcursionCitySeoEnabled.value, seo: citySeoData.value?.seo },
      { enabled: isTourCitySeoEnabled.value, seo: tourCitySeo.value },
      { enabled: isTourRegionSeoEnabled.value, seo: tourRegionSeo.value },
      { enabled: isTourCountrySeoEnabled.value, seo: tourCountrySeo.value },
    ].find(({ enabled }) => enabled)?.seo;
  });

  useHead({
    meta: () => {
      if (!seoData.value?.noindex) {
        return [];
      }

      return [
        {
          name: 'robots',
          content: 'noindex, nofollow',
        },
      ];
    },
  });
}
